import React, { ReactElement, useEffect, useRef, useState } from 'react';

import styles from './Dashboard.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectionFilterView } from '../selectionfilter-view/SelectionFilterView';
import { TpDataContext, TpDataTable } from '../../../data/interfaces';
import {
  getCanonicalTypeInfo,
  clearSelection,
  restrictSelection,
  removeRestriction,
  getColumnInfo,
} from '../../../data/analyticsDataSlice';
import { ModalPortal } from '../../../components/modal-portal/ModalPortal';
import { useAppDispatch, useUser } from '../../../app/hooks';
import { sendEvent, useEventNotification } from '../hooks/events';
import { CreateChartModal } from '../create-chart-modal/CreateChartModal';
import { TpDashboardContext } from './interfaces';
import { convertColumnDataToDisplayString, getSelectedItemCount, smartCountDisplayString } from '../helpers/misc';
import { TopBarPortal } from '../../../components/topbar-portal/TopBarPortal';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { saveSelection } from './actions/saveSelection';
import { useOpenAsset } from '../../../data/assets/helpers';
import { clipboardHasCompatibleData, copySelection, pasteSelection } from '../helpers/clipboard';
import { isEmptyEnSelectionFilter } from '../../../data/selection/selectionEnFilters';
import { getSelectionHighlightColor } from '../../themes/themes';
import { DASHBOARD_ACTION_TRIGGERIDS } from '../actions/executeActionTriggers';
import { useExportFilename } from '../hooks/useExportFilename';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import { TopBarMenu } from './components/TopBarMenu';
import { ViewStateProvider } from '../hooks/ViewStateContext';
import { ViewStateSelector } from './components/ViewStateSelector';

interface TpCanonicalTypeSummaryPopup {
  dataContext: TpDataContext;
  canonicalTypeId: string;
  close: () => void;
}

function CanonicalTypePopup({ dataContext, canonicalTypeId, close }: TpCanonicalTypeSummaryPopup) {
  const dispatch = useAppDispatch();
  const openAsset = useOpenAsset();

  const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, canonicalTypeId);
  const filter = canonicalTypeInfo.selectionFilter;
  const hasFilter = !isEmptyEnSelectionFilter(filter);

  const hasRestriction = !!canonicalTypeInfo.restrictedItemsMap;

  function performClearSelection() {
    dispatch(clearSelection({ dataContextId: dataContext.id, canonicalTypeId: canonicalTypeId }));
    close();
    sendEvent(DASHBOARD_ACTION_TRIGGERIDS.ENTITY_SELECTION_MODIFIED_BY_USER, {
      dataContextId: dataContext.id,
      canonicalTypeId,
    });
  }

  function performRestrictSelection() {
    dispatch(restrictSelection({ dataContextId: dataContext.id, canonicalTypeId: canonicalTypeId }));
    close();
    setTimeout(() => {
      sendEvent('ResetZoomPan', null);
    }, 50);
  }

  function performRemoveRestriction() {
    dispatch(removeRestriction({ dataContextId: dataContext.id, canonicalTypeId: canonicalTypeId }));
    close();
    setTimeout(() => {
      sendEvent('ResetZoomPan', null);
    }, 50);
  }

  function performSaveSelection() {
    close();
    saveSelection(dataContext, canonicalTypeId, dispatch, openAsset);
  }

  function performAddChart() {
    close();
    sendEvent('AddUserChart', { assetId: dataContext.assetId, canonicalTypeId });
  }

  function performDownload() {
    close();
    if (hasRestriction && !hasFilter) {
      sendEvent('RestrictedDownload', { assetId: dataContext.assetId, canonicalTypeId });
    } else if (hasFilter) {
      sendEvent('PartialDownload', { assetId: dataContext.assetId, canonicalTypeId });
    } else {
      sendEvent('Download', { assetId: dataContext.assetId, canonicalTypeId });
    }
  }

  function performCopySelection() {
    close();
    copySelection(canonicalTypeInfo);
  }

  function performPasteSelection() {
    close();
    pasteSelection(dataContext, canonicalTypeInfo, dispatch);
  }

  const userCanAddCharts = true;

  return (
    <div className={styles.menuPopup}>
      <div style={{ padding: '20px' }}>
        <SelectionFilterView dataContext={dataContext} canonicalTypeId={canonicalTypeId} />
      </div>
      <div>
        {hasFilter && (
          <FancyButton iconName="xmark" onClick={performClearSelection}>
            Clear selection
          </FancyButton>
        )}
        {hasFilter && (
          <FancyButton iconName="crop-simple" onClick={performRestrictSelection}>
            Restrict dashboard to selection
          </FancyButton>
        )}
        {hasRestriction && (
          <FancyButton iconName="xmark" onClick={performRemoveRestriction}>
            Remove restriction
          </FancyButton>
        )}
        <FancyButton iconName="download" onClick={performDownload}>
          Download Data
        </FancyButton>
        <div style={{ height: '10px' }} />
        {hasFilter && (
          <FancyButton iconName="clipboard" onClick={performCopySelection}>
            Copy selection
          </FancyButton>
        )}

        {clipboardHasCompatibleData(canonicalTypeInfo) && (
          <>
            <div style={{ height: '10px' }} />
            <FancyButton iconName="paste" onClick={performPasteSelection}>
              Paste selection
            </FancyButton>
          </>
        )}
      </div>

      {userCanAddCharts && (
        <div style={{ position: 'absolute', top: '10px', right: '10px', paddingTop: '20px' }}>
          <FancyButton iconName="chart-line" onClick={performAddChart}>
            Add chart...
          </FancyButton>
        </div>
      )}
    </div>
  );
}

interface TpCanonicalTypeSummaryProps {
  dataContext: TpDataContext;
  canonicalTypeId: string;
  dashboardContext: TpDashboardContext;
  isActive: boolean;
  setActiveFilter: (arg0: string) => void;
}

function CanonicalTypeSummary({
  dataContext,
  canonicalTypeId,
  dashboardContext,
  isActive,
  setActiveFilter,
}: TpCanonicalTypeSummaryProps) {
  const refButton = useRef<HTMLButtonElement>(null);
  const { getFilename } = useExportFilename();

  const [showSelectionFilter, setShowSelectionFilter] = useState(false);
  const [showCreateChartPopup, setShowCreateChartPopup] = useState(false);

  const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, canonicalTypeId);
  const filter = canonicalTypeInfo.selectionFilter;
  const hasFilter = !isEmptyEnSelectionFilter(filter);
  const selectionCount = getSelectedItemCount(canonicalTypeInfo);

  function toggleButton() {
    setActiveFilter(showSelectionFilter ? '' : canonicalTypeId);
  }

  useEffect(() => {
    setShowSelectionFilter(isActive);
  }, [isActive]);

  let totalCount = 0;
  for (const dataTable of dataContext.dataTables)
    if (dataTable.canonicalTypeId == canonicalTypeId) {
      let count = 0;
      for (const itemId of dataTable.columnData[dataTable.colItemId])
        if (!canonicalTypeInfo.restrictedItemsMap || canonicalTypeInfo.restrictedItemsMap[itemId]) count++;
      totalCount = Math.max(totalCount, count);
    }

  const countIndication = hasFilter ? (
    <span>
      {smartCountDisplayString(selectionCount)}/{smartCountDisplayString(totalCount)}
    </span>
  ) : (
    <span>{smartCountDisplayString(totalCount)}</span>
  );

  let filterIcon = <FontAwesomeIcon icon={['fat', 'filter']} />;
  if (hasFilter) filterIcon = <FontAwesomeIcon icon="filter" />;
  if (canonicalTypeInfo.restrictedItemsMap) filterIcon = <FontAwesomeIcon icon="filter-circle-xmark" />;

  const countAndFilterIndication = (
    <div
      style={{
        display: 'inline-block',
        verticalAlign: 'top',
        padding: '2px 5px',
        color:
          hasFilter || canonicalTypeInfo.restrictedItemsMap ? getSelectionHighlightColor().toCanvasString() : undefined,
        backgroundColor:
          hasFilter || canonicalTypeInfo.restrictedItemsMap
            ? getSelectionHighlightColor().opacity(0.1).toCanvasString()
            : undefined,
      }}
    >
      {countIndication}
      <br />
      <div style={{ paddingTop: '5px', fontSize: '13pt' }}>{filterIcon}</div>
    </div>
  );

  useEventNotification(
    'AddUserChart',
    (eventType, eventContent: any) => {
      if (eventContent.assetId == dataContext.assetId && eventContent.canonicalTypeId == canonicalTypeId) {
        setShowCreateChartPopup(true);
      }
    },
    [],
  );

  const downloadDataTableAsTSV = (dataTable: TpDataTable, columnData: any, file_name: string) => {
    const headers = Object.keys(columnData);
    const values = Object.values(columnData);
    const totalRows = (values[0] as any).length;
    const tsvData = [];
    tsvData.push(headers.join('\t'));
    for (let i = 0; i < totalRows; i++) {
      const row = [];
      for (let j = 0; j < headers.length; j++) {
        const colId = headers[j];
        const colInfo = getColumnInfo(dataTable, colId);
        row.push(convertColumnDataToDisplayString(colInfo, (values[j] as any)[i]));
      }
      tsvData.push(row.join('\t'));
    }

    const tsvContent = tsvData.join('\n');
    const blob = new Blob([tsvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', file_name);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getTableFilename = (tableName: string, suffix: string) => {
    return getFilename(
      {
        assetId: dataContext.assetId,
        dataType: 'data',
        title: `${tableName}${suffix}`,
      },
      'tsv',
    );
  };

  useEventNotification(
    'Download',
    (eventType, eventContent: any) => {
      if (eventContent.assetId == dataContext.assetId && eventContent.canonicalTypeId == canonicalTypeId) {
        const canonicalDataContext = dataContext.dataTables.find((dt) => dt.canonicalTypeId == canonicalTypeId);
        if (!canonicalDataContext) return;
        downloadDataTableAsTSV(
          canonicalDataContext,
          canonicalDataContext.columnData,
          getTableFilename(canonicalTypeInfo.name, ''),
        );
      }
    },
    [],
  );

  useEventNotification(
    'PartialDownload',
    (eventType, eventContent: any) => {
      if (eventContent.assetId == dataContext.assetId && eventContent.canonicalTypeId == canonicalTypeId) {
        const canonicalDataContext = dataContext.dataTables.find((dt) => dt.canonicalTypeId == canonicalTypeId);
        if (!canonicalDataContext) return;
        const selectedRows = Object.keys(canonicalTypeInfo.selectedItemsMap as object);
        const selectedIndex = selectedRows.map((row_id) => canonicalTypeInfo.restrictedItemId2Inxdex[row_id]);
        const columnData: any = {};
        for (const [key, value] of Object.entries(canonicalDataContext.columnData)) {
          columnData[key] = columnData[key] = selectedIndex.map((index) => value[index]);
        }
        downloadDataTableAsTSV(canonicalDataContext, columnData, getTableFilename(canonicalTypeInfo.name, '_partial'));
      }
    },
    [],
  );

  useEventNotification(
    'RestrictedDownload',
    (eventType, eventContent: any) => {
      if (eventContent.assetId == dataContext.assetId && eventContent.canonicalTypeId == canonicalTypeId) {
        const canonicalDataContext = dataContext.dataTables.find((dt) => dt.canonicalTypeId == canonicalTypeId);
        if (!canonicalDataContext) return;
        const restrictedRows = Object.keys(canonicalTypeInfo.restrictedItemsMap as object);
        const selectedIndex = restrictedRows.map((row_id) => canonicalTypeInfo.restrictedItemId2Inxdex[row_id]);
        const columnData: any = {};
        for (const [key, value] of Object.entries(canonicalDataContext.columnData)) {
          columnData[key] = columnData[key] = selectedIndex.map((index) => value[index]);
        }
        downloadDataTableAsTSV(
          canonicalDataContext,
          columnData,
          getTableFilename(canonicalTypeInfo.name, '_restricted'),
        );
      }
    },
    [],
  );

  return (
    <div style={{ height: '100%', display: 'inline-block' }}>
      <button ref={refButton} onClick={() => toggleButton()}>
        <div
          style={{
            display: 'inline-block',
            paddingLeft: '10px',
            paddingRight: '12px',
            paddingTop: '4px',
            fontWeight: 'bold',
            verticalAlign: 'top',
            width: '80px',
            whiteSpace: 'normal',
          }}
        >
          {canonicalTypeInfo.name}
        </div>
        {countAndFilterIndication}
      </button>

      {showSelectionFilter && (
        <ModalPortal
          referenceElement={refButton.current}
          offsetX={-15}
          blurTarget={document.getElementById('appClientArea')}
          close={() => toggleButton()}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '30px',
              background: 'var(--light-color)',
              zIndex: 1,
              borderTop: '1px solid var(--gray75)',
            }}
          />
          <CanonicalTypePopup
            dataContext={dataContext}
            canonicalTypeId={canonicalTypeId}
            close={() => toggleButton()}
          />
        </ModalPortal>
      )}

      {showCreateChartPopup && (
        <ModalPortal
          blurTarget={document.getElementById('dashboardClientArea')}
          close={() => setShowCreateChartPopup(false)}
        >
          <CreateChartModal
            dataContext={dataContext}
            canonicalTypeId={canonicalTypeId}
            close={() => setShowCreateChartPopup(false)}
            dashboardContext={dashboardContext}
          />
        </ModalPortal>
      )}
    </div>
  );
}

interface TpTopBarProps {
  dataContext: TpDataContext;
  dashboardContext: TpDashboardContext;
  topBarTables: string[] | null;
  assetInfo: TpAssetInfo;
}

function TopBarContent({ dataContext, dashboardContext, topBarTables, assetInfo }: TpTopBarProps): ReactElement {
  // The dashboard template provides an optional filter on the tables appearing in the <Topbar>.

  // Filter based on canonicalType before passing it on to create the components.
  function assertTopBarElements(dataContext: TpDataContext, topBarTables: string[] | null): string[] {
    const canonicalTypes = Object.keys(dataContext.canonicalTypes);

    if (topBarTables == null) return canonicalTypes.toSorted();

    const assertMatchingCanonicalTypes = topBarTables
      .filter((topBarTable) => {
        const isIncluded = canonicalTypes.includes(topBarTable);

        if (!isIncluded) {
          console.warn(`Key "${topBarTable}" given to DashboardTemplate.topbar, but not found as entityType. Skipped.`);
        }
        return isIncluded;
      })
      .filter((filteredTables, index, array) => {
        // returns on the first occurrence of a string, if index does not match,
        // it means the current value is a duplicate value and is then removed.
        return array.indexOf(filteredTables) == index;
      });

    // The table may or may not be empty at this point.
    // An empty list will give no filtering options and does not crash the application.
    return assertMatchingCanonicalTypes;
  }

  const [activeFilter, setActiveFilter] = useState('');
  const [showDashboardMenu, setShowDashboardMenu] = useState(false);
  const refDashboardMenuButton = useRef<HTMLButtonElement>(null);
  const canonicalTypeidList = assertTopBarElements(dataContext, topBarTables);
  const { user } = useUser();

  // While this is experimental, it is a useful feature to hide the dashboard menu for external users (only write+ access users can see it).
  // ToDo: Make this a user preference in the future, so that users can choose to hide it or not.
  const userCanSeeDashboardMenu = user?.canWrite || user?.isOrganizationAdmin;

  // View state management and load modal: put here as it has to survive mounting/unmounting of the topbarMenu
  const [showSelector, setShowSelector] = useState(false);
  useEventNotification('ShowViewStateSelector', () => setShowSelector(true), []);

  return (
    <TopBarPortal>
      <div className={styles.topBar}>
        {userCanSeeDashboardMenu && (
          <button
            className={styles.topBarMenuButton}
            ref={refDashboardMenuButton}
            onClick={() => setShowDashboardMenu(!showDashboardMenu)}
            aria-label="Dashboard menu"
            title="Dashboard menu"
          >
            <FontAwesomeIcon icon={['fas', 'ellipsis-vertical']} transform="grow-16" />
          </button>
        )}
        {showDashboardMenu && (
          <ModalPortal
            referenceElement={refDashboardMenuButton.current}
            offsetX={-15}
            blurTarget={document.getElementById('appClientArea')}
            close={() => setShowDashboardMenu(false)}
          >
            <TopBarMenu dataContext={dataContext} assetInfo={assetInfo} close={() => setShowDashboardMenu(false)} />
          </ModalPortal>
        )}
        {showSelector && <ViewStateSelector close={() => setShowSelector(false)} />}
        <div
          style={{
            display: 'inline-block',
            verticalAlign: 'top',
            width: '15px',
            height: 'calc(100% - 20px)',
            marginLeft: '15px',
            marginTop: '10px',
            borderLeft: '1px solid var(--base-color)',
          }}
        />
        {canonicalTypeidList.map((canonicalTypeId) => (
          <React.Fragment key={canonicalTypeId}>
            <CanonicalTypeSummary
              dataContext={dataContext}
              canonicalTypeId={canonicalTypeId}
              dashboardContext={dashboardContext}
              isActive={canonicalTypeId == activeFilter}
              setActiveFilter={(filter) => setActiveFilter(filter)}
            />
            <div
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                width: '15px',
                height: 'calc(100% - 20px)',
                marginLeft: '15px',
                marginTop: '10px',
                borderLeft: '1px solid var(--base-color)',
              }}
            />
          </React.Fragment>
        ))}
      </div>
    </TopBarPortal>
  );
}

function TopBar({ dataContext, dashboardContext, topBarTables, assetInfo }: TpTopBarProps): ReactElement {
  return (
    <ViewStateProvider assetInfo={assetInfo} dataContext={dataContext}>
      <TopBarContent
        dataContext={dataContext}
        dashboardContext={dashboardContext}
        topBarTables={topBarTables}
        assetInfo={assetInfo}
      />
    </ViewStateProvider>
  );
}

export default TopBar;
